import { useCommand } from "@/components/command/command-provider";
import { DocumentTypeIcon } from "@/components/document/document-type-icon";
import { SettingsDialogContent } from "@/components/settings/settings-dialog-content";
import { FirstTimeUserDialog } from "@/components/onboarding/first-time-user-dialog";
import { useOnboarding } from "@/hooks/use-onboarding";
import { Badge } from "@/components/ui/badge";
import { Dialog } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { UserAvatar } from "@/components/user/user-avatar";
import { useUser } from "@/contexts/UserProvider";
import { useIntegration } from "@/contexts/IntegrationProvider";
import {
  authService,
  documentService,
  projectService,
  roomService,
  supabase,
} from "@/lib/supabase";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  redirect,
  useRouter,
} from "@tanstack/react-router";
import {
  ArrowRight,
  ChevronDown,
  CircleQuestionMarkIcon,
  CircleUser,
  FolderLock,
  Import,
  Loader2,
  LogOut,
  MessageSquarePlus,
  MoreHorizontal,
  PenLine,
  Plus,
  Settings,
  Trash2,
  Users,
  X,
  Zap,
} from "lucide-react";
import { overlay } from "overlay-kit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryState } from "nuqs";
import { DocumentDetailWidget } from "@/components/document/document-detail-widget";
import { Button } from "@/components/ui/button";
import { RoomWidget } from "@/components/room/room-widget";
import { DocumentCreateDialogContent } from "@/components/document/document-create-dialog-content";
import { DocumentEditDialogContent } from "@/components/document/document-edit-dialog-content";
import { toast } from "sonner";
import { DocumentImportDialogContent } from "@/components/document/document-import-dialog-content";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCommandRegistration } from "@/hooks/use-command-registration";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { QueryParmsKey } from "@/constants/query-params";
import { Skeleton } from "@/components/ui/skeleton";
import { Trans } from "react-i18next";
import { RootErrorComponent } from "@/routes/__root";
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import SlackLogo from "@/assets/slack_logo.svg?react";
import { ChangeProposalDialog } from "@/components/chat/change-proposal-dialog";
import { clipboard } from "@/utils/clipboard";
import { UserNameText } from "@/components/user/user-name-text";
import { GUIDE_NOTION_URL } from "../../../constants/urls";

export const Route = createFileRoute("/_authenticated/$projectId/")({
  component: RouteComponent,
  errorComponent: ({ error }) => {
    if (error.message === "unauthorized") return <UnauthorizedErrorComponent />;
    return <RootErrorComponent error={error} />;
  },
  beforeLoad: async ({
    context: { authentication },
    params: { projectId },
  }) => {
    // Check authentication
    const user = authentication?.user;
    if (!user) throw redirect({ to: "/login" });

    // Check authorization
    const userProjects = await projectService.getUserProjects(user.id);
    const isAuthorized = userProjects.some(({ id }) => id === projectId);
    if (!isAuthorized) throw Error("unauthorized");

    // Check user current project
    if (user.currentProjectId && user.currentProjectId !== projectId) {
      await projectService.switchCurrentProject(projectId, user.id);
    }
  },
  loader: async ({ context: { authentication } }) => {
    const userId = authentication?.user?.id;
    if (!userId) throw redirect({ to: "/login" });

    try {
      return await projectService.getCurrentProject(userId);
    } catch {
      throw redirect({
        to: "/project-create-or-join",
        replace: true,
      });
    }
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        title: loaderData?.name ? `${loaderData.name} - Palman` : "Palman",
      },
    ],
  }),
});

function RouteComponent() {
  const router = useRouter();
  const queryParams = Route.useParams();
  const { projectId } = queryParams;
  const { queryClient } = Route.useRouteContext();
  const isMobile = useIsMobile();

  const { user, refreshUser } = useUser();
  const userId = user.id;
  const { openCommandPalette } = useCommand();
  const { slack } = useIntegration();

  useCommandRegistration({ pageType: "global" });

  // State for managing Slack nudge card dismissal
  const [isSlackNudgeDismissed, setIsSlackNudgeDismissed] = useState<boolean>(
    () => {
      try {
        return (
          localStorage.getItem(
            ["slackNudgeDismissed", userId, projectId].join(";"),
          ) === "true"
        );
      } catch {
        return false;
      }
    },
  );

  const handleDismissSlackNudge = useCallback(() => {
    setIsSlackNudgeDismissed(true);
    try {
      localStorage.setItem(
        ["slackNudgeDismissed", userId, projectId].join(";"),
        "true",
      );
    } catch {
      // Ignore localStorage errors
    }
  }, [userId, projectId]);

  // Show Slack nudge card if:
  // 1. Not dismissed by user
  // 2. Slack is not connected
  const shouldShowSlackNudge = !isSlackNudgeDismissed && !slack.connected;

  const { data: currentProject, refetch } = useQuery({
    queryKey: ["projectService", "getCurrentProject", userId],
    queryFn: ({ queryKey: [, , userId] }) =>
      projectService.getCurrentProject(userId),
  });

  const { data: userProjects } = useQuery({
    queryKey: ["projectService", "getUserProjects"],
    queryFn: () => projectService.getUserProjects(userId),
  });

  const { mutate: switchCurrentProject } = useMutation({
    mutationFn: ({ projectId }: { projectId: string }) =>
      projectService.switchCurrentProject(projectId, userId),
    onSuccess: async (_, { projectId }) => {
      refetch();

      queryClient.invalidateQueries();
      refreshUser();

      await router.navigate({
        to: "/$projectId",
        params: { projectId },
        replace: true,
      });

      await setRoomId(null);
    },
  });

  const { data: userInvitationData } = useQuery({
    queryKey: ["projectService", "getUserInvitations"],
    queryFn: () => projectService.getUserInvitations(user.email, "pending"),
  });
  const isInvitedProjectExist =
    userInvitationData && userInvitationData.length > 0;

  const [documentId, setDocumentId] = useQueryState(QueryParmsKey.DOCUMENT_ID);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setChunkId] = useQueryState(QueryParmsKey.CHUNK_ID);
  const handleDocumentId = (id: string | null) => {
    setDocumentId(id);
    setChunkId(null);
  };

  const {
    data: documents,
    isLoading: isDocumentsLoading,
    isSuccess: isDocumentsSuccess,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["documentService", "getProjectDocumentsPaginated", projectId],
    queryFn: ({ queryKey: [, , projectId], pageParam = 1 }) =>
      documentService.getProjectDocumentsPaginated(projectId, userId, "all", {
        page: pageParam,
        limit: 50,
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination.hasNextPage) {
        return lastPage.pagination.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
  });

  const deleteDocumentMutation = useMutation({
    mutationFn: (documentId: string) =>
      documentService.deleteDocument(documentId, userId),
    onError: (error) => {
      toast.error(
        <Trans i18nKey={"deleteDocument.error"}>
          Failed to delete document
        </Trans>,
        {
          description: error.message,
        },
      );
    },

    onSuccess: (_, id) => {
      if (documentId === id) handleDocumentId(null);
      queryClient.invalidateQueries({
        queryKey: ["documentService", "getDocument", documentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["documentService", "getProjectDocuments", projectId],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "documentService",
          "getProjectDocumentsPaginated",
          projectId,
        ],
      });
      toast.success(
        <Trans i18nKey={"deleteDocument.success"}>Document deleted</Trans>,
      );
    },
  });
  const isMac = useMemo(() => {
    if (typeof window === "undefined") return false;
    return (
      navigator.platform.toUpperCase().indexOf("MAC") >= 0 ||
      navigator.userAgent.toUpperCase().indexOf("MAC") >= 0
    );
  }, []);

  const [roomId, setRoomId] = useQueryState(QueryParmsKey.ROOM_ID);
  const createRoomMutation = useMutation({
    mutationFn: ({ projectId }: { projectId: string }) =>
      roomService.createRoom(
        {
          projectId,
          name: new Date().toDateString(),
        },
        userId,
      ),
    onSuccess: ({ id }) => {
      setRoomId(id);
    },
  });

  // Get flattened documents list
  const flattenedDocuments = useMemo(() => {
    if (!documents?.pages) return [];
    return documents.pages.flatMap((page) => page.data);
  }, [documents?.pages]);

  // Initialize onboarding
  const { showOnboarding, setShowOnboarding, createSampleDocument } =
    useOnboarding({
      projectId,
      documents: flattenedDocuments,
      userProjects,
    });

  const handleOnboardingConfirm = async () => {
    try {
      await createSampleDocument();

      // Refetch suggested questions after creating sample document
      queryClient.invalidateQueries({
        queryKey: ["projectService", "getSuggestedQuestions", projectId],
      });
    } catch (error) {
      console.error("Failed to create onboarding document:", error);
    }
  };

  const handleOnboardingSkip = async () => {
    try {
      // Get current session
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Authentication required");
      }

      // Call Edge Function to mark onboarding as completed
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/complete-onboarding`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ projectId }),
        },
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to complete onboarding");
      }

      // Refresh project data
      queryClient.invalidateQueries({
        queryKey: ["projectService", "getProject", projectId],
      });
      queryClient.invalidateQueries({
        queryKey: ["projectService", "getCurrentProject", userId],
      });

      setShowOnboarding(false);
    } catch (error) {
      console.error("Failed to update onboarding status:", error);
    }
  };

  const { data: roomData, isSuccess: isRoomSuccess } = useQuery({
    queryKey: ["roomService", "getRoom", roomId],
    queryFn: ({ queryKey: [, , roomId] }) =>
      roomService.getRoom(roomId!, userId),
    enabled: !!roomId,
  });

  useEffect(() => {
    if (!roomId) {
      createRoomMutation.mutate({ projectId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, projectId]);

  // Infinite scroll ref and logic
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastDocumentElementRef = useCallback(
    (node: HTMLElement | null) => {
      if (isFetchingNextPage) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });
      if (node) observerRef.current.observe(node);
    },
    [isFetchingNextPage, fetchNextPage, hasNextPage],
  );

  // Set up real-time subscription for document loading status changes
  useEffect(() => {
    if (!projectId || !userId) return;

    let unsubscribe: (() => void) | null = null;

    // Subscribe to document updates
    documentService
      .subscribeToProjectDocuments(projectId, userId, {
        onUpdate: (document) => {
          // Check if document finished loading (is_loading changed to false)
          if (document.isLoading === false) {
            // Document finished loading, invalidate the query to refresh the list
            queryClient.invalidateQueries({
              queryKey: [
                "documentService",
                "getProjectDocumentsPaginated",
                projectId,
              ],
            });
          }
        },
        onInsert: () => {
          // New document added, refresh the list
          queryClient.invalidateQueries({
            queryKey: [
              "documentService",
              "getProjectDocumentsPaginated",
              projectId,
            ],
          });
        },
        onDelete: () => {
          // Document deleted, refresh the list
          queryClient.invalidateQueries({
            queryKey: [
              "documentService",
              "getProjectDocumentsPaginated",
              projectId,
            ],
          });
        },
      })
      .then((unsub) => {
        unsubscribe = unsub;
      })
      .catch((error) => {
        console.error("Failed to subscribe to document updates:", error);
      });

    // Cleanup subscription on unmount or when projectId/userId changes
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [projectId, userId, queryClient]);

  // chat proposal dialog
  const [changeProposalId, setChangeProposalId] = useQueryState(
    QueryParmsKey.CHANGE_PROPOSAL_ID,
  );
  return (
    <>
      <FirstTimeUserDialog
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onConfirm={handleOnboardingConfirm}
        onSkip={handleOnboardingSkip}
      />
      <ChangeProposalDialog
        proposalId={changeProposalId}
        open={!!changeProposalId}
        onOpenChange={(open) => {
          if (!open) {
            setChangeProposalId(null);
          }
        }}
      />
      <SidebarProvider>
        <Sidebar>
          <SidebarHeader>
            <SidebarMenu>
              <SidebarMenuItem>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <SidebarMenuButton>
                      {currentProject?.name}
                      <ChevronDown className="ml-auto" />
                    </SidebarMenuButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-(--radix-popper-anchor-width)">
                    <DropdownMenuItem
                      onClick={() =>
                        overlay.open(({ isOpen, close }) => (
                          <Dialog open={isOpen} onOpenChange={close}>
                            <SettingsDialogContent initialTab="general" />
                          </Dialog>
                        ))
                      }
                    >
                      <Settings />
                      <Trans i18nKey={"sidebar.settings"}>Settings</Trans>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() =>
                        overlay.open(({ isOpen, close }) => (
                          <Dialog open={isOpen} onOpenChange={close}>
                            <SettingsDialogContent initialTab="users" />
                          </Dialog>
                        ))
                      }
                    >
                      <Users />
                      <Trans i18nKey={"sidebar.manageMembers"}>
                        Manage members
                      </Trans>
                    </DropdownMenuItem>
                    <a href={GUIDE_NOTION_URL} target="_blank">
                      <DropdownMenuItem>
                        <CircleQuestionMarkIcon />
                        <Trans i18nKey={"sidebar.guide"}>Guide</Trans>
                      </DropdownMenuItem>
                    </a>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>
                      <Trans i18nKey={"sidebar.projects"}>Projects</Trans>
                    </DropdownMenuLabel>
                    {userProjects?.map(
                      ({ id: projectId, name: projectName }) => (
                        <DropdownMenuCheckboxItem
                          key={projectId}
                          checked={projectId === currentProject?.id}
                          onClick={() => switchCurrentProject({ projectId })}
                        >
                          {projectName}
                        </DropdownMenuCheckboxItem>
                      ),
                    )}
                    <DropdownMenuSeparator />
                    <Link to={"/project-create-or-join"}>
                      <DropdownMenuItem>
                        <Trans i18nKey={"sidebar.createOrJoinProject"}>
                          Create or join project
                        </Trans>
                        {isInvitedProjectExist ? (
                          <Badge
                            variant="destructive"
                            className="rounded-full text-xs px-1.5 py-0"
                          >
                            {userInvitationData.length}
                          </Badge>
                        ) : null}
                      </DropdownMenuItem>
                    </Link>
                  </DropdownMenuContent>
                </DropdownMenu>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton onClick={openCommandPalette}>
                  <Zap />
                  <Trans i18nKey={"sidebar.commandBar"}>Command bar</Trans>
                </SidebarMenuButton>
                <SidebarMenuBadge className="space-x-1">
                  {isMac ? <kbd className="text-base">⌘</kbd> : <kbd>Ctrl</kbd>}
                  <kbd>K</kbd>
                </SidebarMenuBadge>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarHeader>
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupLabel>
                <Trans i18nKey={"sidebar.documents"}>Documents</Trans>
                {isDocumentsSuccess &&
                  documents.pages[0]?.pagination?.total !== undefined && (
                    <span className="ml-1">
                      ({documents.pages[0].pagination.total})
                    </span>
                  )}
              </SidebarGroupLabel>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarGroupAction>
                    <Plus />{" "}
                    <span className="sr-only">
                      <Trans i18nKey={"sidebar.importDocuments"}>
                        Import documents
                      </Trans>
                    </span>
                  </SidebarGroupAction>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem
                    onClick={() => {
                      overlay.open(({ isOpen, close }) => (
                        <Dialog open={isOpen} onOpenChange={close}>
                          <DocumentImportDialogContent
                            projectId={projectId}
                            onSuccess={() => {
                              toast.success(
                                <Trans i18nKey={"importDocument.success"}>
                                  Document created
                                </Trans>,
                              );

                              queryClient.invalidateQueries({
                                queryKey: [
                                  "documentService",
                                  "getProjectDocumentsPaginated",
                                  projectId,
                                ],
                              });
                              close();
                            }}
                          />
                        </Dialog>
                      ));
                    }}
                  >
                    <Import />
                    <Trans i18nKey={"sidebar.import"}>Import</Trans>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      overlay.open(({ isOpen, close }) => (
                        <Dialog open={isOpen} onOpenChange={close}>
                          <DocumentCreateDialogContent
                            projectId={projectId}
                            onSuccess={() => {
                              toast.success(
                                <Trans i18nKey={"createDocument.success"}>
                                  Document created
                                </Trans>,
                              );
                              queryClient.invalidateQueries({
                                queryKey: [
                                  "documentService",
                                  "getProjectDocumentsPaginated",
                                  projectId,
                                ],
                              });

                              close();
                            }}
                          />
                        </Dialog>
                      ));
                    }}
                  >
                    <PenLine />
                    <Trans i18nKey={"sidebar.create"}>Create</Trans>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <SidebarGroupContent>
                <SidebarMenu>
                  {isDocumentsLoading ? (
                    <SidebarMenuItem>
                      <SidebarMenuButton>
                        <Loader2 className="animate-spin" />
                        <Skeleton className="h-5 w-25" />
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ) : isDocumentsSuccess ? (
                    <>
                      {documents.pages.map((page, pageIndex) =>
                        page.data.map((item, documentIndex) => {
                          const { id, type, title, isLoading, isOnboarding } =
                            item;
                          const isLastDocument =
                            pageIndex === documents.pages.length - 1 &&
                            documentIndex === page.data.length - 1;

                          return (
                            <SidebarMenuItem
                              key={id}
                              ref={
                                isLastDocument ? lastDocumentElementRef : null
                              }
                              className={
                                isOnboarding
                                  ? "onboarding-document rounded-md"
                                  : ""
                              }
                            >
                              <SidebarMenuButton
                                isActive={id === documentId}
                                onClick={(e) => {
                                  if (isLoading) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    return;
                                  }
                                  handleDocumentId(id);
                                }}
                                disabled={isLoading}
                                className={
                                  isLoading
                                    ? "opacity-50 cursor-not-allowed pointer-events-none"
                                    : ""
                                }
                              >
                                {isLoading ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <DocumentTypeIcon documentType={type} />
                                )}
                                <span className="truncate">{title}</span>
                              </SidebarMenuButton>
                              <DropdownMenu>
                                <DropdownMenuTrigger
                                  asChild
                                  disabled={isLoading}
                                >
                                  <SidebarMenuAction showOnHover>
                                    <MoreHorizontal />
                                    <span className="sr-only">
                                      <Trans i18nKey={"sidebar.more"}>
                                        More
                                      </Trans>
                                    </span>
                                  </SidebarMenuAction>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent>
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      // Fetch full document content first
                                      const fullDocument =
                                        await documentService.getDocument(
                                          id,
                                          userId,
                                        );

                                      overlay.open(({ isOpen, close }) => (
                                        <Dialog
                                          open={isOpen}
                                          onOpenChange={close}
                                        >
                                          <DocumentEditDialogContent
                                            document={{
                                              id,
                                              title: fullDocument.title,
                                              content:
                                                fullDocument.content || "",
                                              projectId,
                                            }}
                                            onSuccess={() => {
                                              queryClient.invalidateQueries({
                                                queryKey: [
                                                  "documentService",
                                                  "getDocument",
                                                  id,
                                                ],
                                              });
                                              queryClient.invalidateQueries({
                                                queryKey: [
                                                  "documentService",
                                                  "getProjectDocuments",
                                                  projectId,
                                                ],
                                              });
                                              queryClient.invalidateQueries({
                                                queryKey: [
                                                  "documentService",
                                                  "getProjectDocumentsPaginated",
                                                  projectId,
                                                ],
                                              });
                                              toast.success(
                                                <Trans
                                                  i18nKey={
                                                    "editDocument.success"
                                                  }
                                                >
                                                  Document updated
                                                </Trans>,
                                              );
                                              handleDocumentId(null);
                                              close();
                                            }}
                                            onError={(error) => {
                                              toast.error(
                                                <Trans
                                                  i18nKey={"editDocument.error"}
                                                >
                                                  Failed to update document
                                                </Trans>,
                                                {
                                                  description: error.message,
                                                },
                                              );
                                            }}
                                          />
                                        </Dialog>
                                      ));
                                    }}
                                  >
                                    <PenLine className="text-muted-foreground" />
                                    <span>
                                      <Trans i18nKey={"sidebar.editDocument"}>
                                        Edit
                                      </Trans>
                                    </span>
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    variant="destructive"
                                    onClick={() =>
                                      overlay.open(({ isOpen, close }) => (
                                        <AlertDialog
                                          open={isOpen}
                                          onOpenChange={close}
                                        >
                                          <AlertDialogContent>
                                            <AlertDialogHeader>
                                              <AlertDialogTitle>
                                                <Trans
                                                  i18nKey={
                                                    "documentDeleteAlertDialog.title"
                                                  }
                                                >
                                                  Are you absolutely sure?
                                                </Trans>
                                              </AlertDialogTitle>
                                              <AlertDialogDescription>
                                                <Trans
                                                  i18nKey={
                                                    "documentDeleteAlertDialog.description"
                                                  }
                                                >
                                                  This action cannot be undone.
                                                  This will permanently delete
                                                  your document and remove your
                                                  data from our servers.
                                                </Trans>
                                              </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                              <AlertDialogCancel>
                                                <Trans
                                                  i18nKey={
                                                    "documentDeleteAlertDialog.cancel"
                                                  }
                                                >
                                                  Cancel
                                                </Trans>
                                              </AlertDialogCancel>
                                              <AlertDialogAction
                                                variant="destructive"
                                                onClick={() => {
                                                  deleteDocumentMutation.mutate(
                                                    id,
                                                  );
                                                }}
                                              >
                                                <Trans
                                                  i18nKey={
                                                    "documentDeleteAlertDialog.delete"
                                                  }
                                                >
                                                  Delete
                                                </Trans>
                                              </AlertDialogAction>
                                            </AlertDialogFooter>
                                          </AlertDialogContent>
                                        </AlertDialog>
                                      ))
                                    }
                                  >
                                    <Trash2 className="text-muted-foreground" />
                                    <span>
                                      <Trans i18nKey={"sidebar.delete"}>
                                        Delete
                                      </Trans>
                                    </span>
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </SidebarMenuItem>
                          );
                        }),
                      )}
                      {isFetchingNextPage && (
                        <SidebarMenuItem>
                          <SidebarMenuButton disabled>
                            <Loader2 className="animate-spin" />
                            <span>
                              <Trans i18nKey={"sidebar.loadMore"}>
                                Load more...
                              </Trans>
                            </span>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      )}
                    </>
                  ) : null}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
          <SidebarFooter>
            {shouldShowSlackNudge && (
              <Card className="py-4 relative">
                <Button
                  size="icon"
                  variant={"ghost"}
                  className="absolute top-1 right-1"
                  onClick={handleDismissSlackNudge}
                >
                  <X />
                </Button>
                <CardHeader className="px-4">
                  <CardTitle className="flex gap-x-2 items-center">
                    <SlackLogo className="size-3 inline" />
                    <Trans i18nKey={"slackNudgeCard.title"}>
                      Connect Slack
                    </Trans>
                  </CardTitle>
                  <CardDescription>
                    <Trans i18nKey={"slackNudgeCard.description"}>
                      Try Slack integration to use Palman in your workspace
                    </Trans>
                  </CardDescription>
                </CardHeader>
                <CardFooter className="px-4 ">
                  <Button
                    size={"sm"}
                    variant={"secondary"}
                    className="w-full"
                    onClick={() =>
                      overlay.open(({ isOpen, close }) => (
                        <Dialog open={isOpen} onOpenChange={close}>
                          <SettingsDialogContent initialTab="integrations" />
                        </Dialog>
                      ))
                    }
                  >
                    <Trans i18nKey={"slackNudgeCard.buttonText"}>
                      Open integration
                    </Trans>
                    <ArrowRight />
                  </Button>
                </CardFooter>
              </Card>
            )}
            <SidebarMenu>
              <SidebarMenuItem>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <SidebarMenuButton
                      size="lg"
                      className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                    >
                      <UserAvatar userId={user.id} />
                      <div className="grid flex-1 text-left text-sm leading-tight">
                        <span className="truncate">
                          {user.fullName || user.email}
                        </span>
                        <span className="truncate text-xs text-muted-foreground">
                          {user.email}
                        </span>
                      </div>
                    </SidebarMenuButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                    side="top"
                    align="end"
                    sideOffset={4}
                  >
                    <DropdownMenuItem
                      onClick={() =>
                        overlay.open(({ isOpen, close }) => (
                          <Dialog open={isOpen} onOpenChange={close}>
                            <SettingsDialogContent initialTab={"account"} />
                          </Dialog>
                        ))
                      }
                    >
                      <CircleUser />
                      <Trans i18nKey={"sidebar.account"}>Account</Trans>
                    </DropdownMenuItem>

                    <DropdownMenuItem onClick={() => authService.signOut()}>
                      <LogOut />
                      <Trans i18nKey={"sidebar.logOut"}>Log out</Trans>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarFooter>
        </Sidebar>
        <SidebarInset>
          <ResizablePanelGroup direction="horizontal">
            {documentId ? (
              <>
                <ResizablePanel
                  id={"palman-document-panel"}
                  minSize={30}
                  order={0}
                  className="flex flex-col"
                >
                  <div className="flex items-center p-2 pl-2 md:pl-5 border-b min-h-14">
                    {isMobile ? <SidebarTrigger /> : null}
                    <span className="flex-1">
                      <Trans i18nKey={"documentPanel.document"}>Document</Trans>
                    </span>
                    <Button
                      size={"icon"}
                      variant={"ghost"}
                      onClick={() => handleDocumentId(null)}
                    >
                      <X />
                    </Button>
                  </div>
                  <div className="flex-1 overflow-auto px-5 py-5">
                    <DocumentDetailWidget documentId={documentId} />
                  </div>
                </ResizablePanel>
                <ResizableHandle withHandle hidden={isMobile && !!documentId} />
              </>
            ) : null}
            <ResizablePanel
              id={"palman-room-panel"}
              order={1}
              minSize={30}
              className="flex flex-col"
              hidden={isMobile && !!documentId}
            >
              <div className="flex items-center p-2 pl-2 md:pl-5 border-b min-h-14">
                {isMobile ? <SidebarTrigger /> : null}
                <span className="flex-1">
                  <Trans i18nKey={"askPanel.ask"}>Ask</Trans>
                </span>
                {isRoomSuccess && roomData.userId !== userId ? (
                  <div>
                    <Badge variant={"secondary"}>
                      <Trans i18nKey={"roomWidget.shared.createdBy"}>
                        Created by <UserNameText userId={roomData.userId} />
                      </Trans>
                    </Badge>
                  </div>
                ) : null}
                {isRoomSuccess &&
                roomData.userId === userId &&
                roomData.lastMessageAt ? (
                  <Button
                    variant={"ghost"}
                    onClick={() => {
                      const url = new URL(window.location.href);
                      const roomIdParam = url.searchParams.get("r");

                      const cleanUrl = new URL(url.origin + url.pathname);
                      if (roomIdParam) {
                        cleanUrl.searchParams.set("r", roomIdParam);
                      }

                      clipboard.writeText(cleanUrl.toString());
                      toast.success(
                        <Trans i18nKey={"askPanel.shareLinkCopied"}>
                          Link copied to clipboard
                        </Trans>,
                      );
                    }}
                  >
                    <Trans i18nKey={"askPanel.share"}>Share</Trans>
                  </Button>
                ) : null}
                {isRoomSuccess &&
                roomData.userId === userId &&
                roomData.lastMessageAt ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size={"icon"}
                        variant={"ghost"}
                        onClick={() => {
                          setRoomId(null);
                          setChunkId(null);
                        }}
                      >
                        <MessageSquarePlus />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <Trans i18nKey={"askPanel.newChat"}>New chat</Trans>
                    </TooltipContent>
                  </Tooltip>
                ) : null}
              </div>
              {roomId ? (
                <div className="flex-1 overflow-auto">
                  <RoomWidget roomId={roomId} />
                </div>
              ) : null}
            </ResizablePanel>
          </ResizablePanelGroup>
        </SidebarInset>
      </SidebarProvider>
    </>
  );
}

function UnauthorizedErrorComponent() {
  const { user } = useUser();

  return (
    <>
      <div className="flex h-screen items-center justify-center p-4">
        <div className="max-w-md text-center">
          <FolderLock className="mx-auto h-12 w-12 mb-4" />
          <h1 className="text-2xl font-bold mb-2">
            <Trans i18nKey={"unauthorizedErrorComponent.title"}>
              No access to this project
            </Trans>
          </h1>
          <p className="text-muted-foreground mb-6">
            <Trans i18nKey={"unauthorizedErrorComponent.description"}>
              You don't have access to this project. Please contact a project
              owner for access.
            </Trans>
          </p>
          <Link to="/login">
            <Button>
              <Trans i18nKey={"unauthorizedErrorComponent.buttonText"}>
                Go back
              </Trans>
            </Button>
          </Link>
        </div>
      </div>
      <div className="fixed top-6 right-5">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost">{user.email}</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)]">
            <DropdownMenuItem onClick={() => authService.signOut()}>
              <LogOut />
              <Trans i18nKey={"unauthorizedErrorComponent.logOut"}>
                Log out
              </Trans>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
}
